import useSWR from 'swr';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from 'providers/AppProvider';
import { getFormValues, isEmail } from 'helpers';

type MemberValues = { [ssn: string]: string };

type Member = {
	name: string;
	roles: string;
	ssn: string;
};

export function useSigningAuthority() {
	const ctx = useContext(AppContext);
	const [membersURL, setMembersURL] = useState<string | null>(null);
	const [signingAuthorityURL, setSigningAuthorityURL] = useState<string | null>(null);
	const { config, http, state, goToNextPage, t } = ctx;
	const { API, IS_EMPLOYEE_WEB } = config;
	const { context, user } = state;
	const processId = context?.processInfo?.agreementProcessId;
	const ssn = user.kennitala;
	const [errors, setErrors] = useState({} as any);
	const { data } = useSWR<{ signingAuthority: string }>(signingAuthorityURL);
	const membersSWR = useSWR<{ members: Member[] }>(membersURL);
	const members = membersSWR.data?.members;

	const validate = useCallback(
		(e: any) => {
			e.preventDefault();
			e.stopPropagation();
			const copy = { ...errors };
			delete copy[e.target.name];
			const err = isEmail(e.target.value) ? copy : { ...copy, [e.target.name]: 'error_email' };
			setErrors(err);
		},
		[errors]
	);

	const submit = useCallback(
		(e: any) => {
			e.preventDefault();
			e.stopPropagation();
			const form = document.querySelector('form');
			if (form && members) {
				const values = getFormValues(form) as MemberValues;
				const newErrors = formErrors(members, values);
				if (Object.keys(newErrors).length) {
					return setErrors(newErrors);
				}
				const msg = t('signing_authority_submit_label');
				const payload = {
					ssn,
					members: members.map(member => ({
						email: values[member.ssn],
						kennitala: member.ssn
					}))
				};
				http.post(`${API.member}/${processId}/StoreMembers`, payload, msg).then(goToNextPage);
			}
		},
		[members, ssn, http, API, processId, t, goToNextPage]
	);

	useEffect(() => {
		if (IS_EMPLOYEE_WEB && user.isManualSignCompany && user.companyKennitala) {
			return goToNextPage();
		}
		const qs = IS_EMPLOYEE_WEB ? `?ssn=${ssn}` : '';
		setMembersURL(`${API.signingAuthorityMembers}/${processId}${qs}`);
		setSigningAuthorityURL(`${API.signingAuthority}/${processId}${qs}`);
	}, []); // eslint-disable-line

	return {
		...ctx,
		data,
		errors,
		members,
		submit,
		validate
	};
}

function formErrors(members: Member[], values: MemberValues) {
	return members.reduce((err: any, m) => {
		return isEmail(values[m.ssn]) ? err : { ...err, [m.ssn]: 'error_email' };
	}, {});
}
